'use client';

import { useCallback, useEffect, useRef } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import useSWRMutation from 'swr/mutation';
import useSWRInfinite, { type SWRInfiniteConfiguration } from 'swr/infinite';
import customFetch from '@/lib/utils/fetch';

// ── 类型定义（字段取自接口文档实际返回，未穷举 Stripe 原始对象全部字段） ──────

export interface StripePriceSettings {
  forge: Record<string, string>;
  search: Record<string, string>;
}

export interface StripePrice {
  id: string;
  object: string;
  active: boolean;
  currency: string;
  nickname: string | null;
  product: { id: string; name: string; description?: string | null } | string;
  recurring: { interval: string; interval_count: number } | null;
  type: 'one_time' | 'recurring';
  unit_amount: number;
  unit_amount_decimal: string;
  metadata?: Record<string, string>;
}

export interface PriceQueryParams {
  /**
   * settings 里的分组名（'forge' 或 'search'）。传了这个，服务端会忽略下面
   * limit/product/active，改为按分组批量返回该分组下所有已配置价格的详情。
   */
  key?: 'forge' | 'search';
  /** 默认 10，服务端未做上限校验；传了 key 时会被忽略 */
  limit?: number;
  /** 按 Stripe product ID 过滤；传了 key 时会被忽略 */
  product?: string;
  /** 传了才会带上该筛选条件；传了 key 时会被忽略 */
  active?: boolean;
}

export interface StripeCheckoutSession {
  id: string;
  object: string;
  mode: 'payment' | 'subscription' | 'setup';
  status: string;
  payment_status: string;
  url: string | null;
  amount_total: number | null;
  currency: string | null;
  customer: string | null;
  subscription?: string | null;
  metadata?: Record<string, string>;
  [key: string]: unknown;
}

export interface StripeUpgradeQuote {
  current_price_id: string;
  target_price_id: string;
  estimated_upgrade_price: number;
  current_price_amount: number;
  target_price_amount: number;
  remaining_value: number;
  remaining_days: number;
  current_subscription: {
    stripe_subscription_id: string;
    plan_type: string;
    current_period_start: string;
    current_period_end: string;
  };
  new_subscription: {
    stripe_subscription_id: string;
    plan_type: string;
    current_period_start: string;
    current_period_end: string;
  };
}

interface PriceListResponse {
  prices: StripePrice[];
}

interface CreateCheckoutResponse {
  session: StripeCheckoutSession;
  url: string;
  publicKey: string;
}

interface CheckCheckoutResponse {
  session: StripeCheckoutSession;
}

interface CancelSubscriptionResponse {
  success: boolean;
  sessionCancelled: Record<string, unknown>;
}

interface UpgradeQuoteResponse {
  success: boolean;
  data: StripeUpgradeQuote;
}

// 升级差价是一次性 Checkout Session（mode='payment'），不是发票；跟创建支付返回结构一致
type UpgradeExecuteResponse = CreateCheckoutResponse;

export interface CreateCheckoutPayload {
  /** mode='setup' 时不需要也不能传 line_items（只绑定支付方式，不产生扣款） */
  line_items?: { price: string; quantity: number }[];
  mode: 'subscription' | 'payment' | 'setup';
  success_url: string;
  cancel_url: string;
  metadata?: Record<string, string | number>;
}

export interface ExecuteUpgradePayload {
  current_price_id: string;
  target_price_id: string;
  success_url: string;
  cancel_url: string;
}

export interface UseStripeOptions {
  /** 传 true 拉取商品设置 GET /api/stripe/prices/settings（该接口不接受任何参数）；不传则跳过 */
  priceSettings?: boolean;
  /**
   * 传 true 或参数对象请求 GET /api/stripe/prices；不传则跳过。
   * 传 `{ key: 'forge' | 'search' }` 按 settings 分组批量查询；传 limit/product/active 走列表查询。
   */
  prices?: PriceQueryParams | boolean;
  /** 商品价格 ID，传入时请求 GET /api/stripe/prices/:id */
  priceId?: string | null;
  /** 支付会话 ID，传入时请求 GET /api/stripe/checkout?session_id=，用于支付后检查状态 */
  checkoutSessionId?: string | null;
  /**
   * 传 true 或 { targetPriceId } 拉取升级报价 GET /api/stripe/subscriptions/upgrade-payment。
   * 不传 targetPriceId 时由后端按当前订阅自动推导（月→月、年→年）。
   */
  upgradeQuote?: { targetPriceId?: string } | boolean;
  swr?: SWRConfiguration;
}

/**
 * 封装 /api/stripe/* 相关接口：商品设置、商品价格（列表/单个）、创建/检查支付、取消订阅、订阅升级报价与执行。
 */
export function useStripe(options: UseStripeOptions = {}) {
  const {
    priceSettings = false,
    prices = false,
    priceId = null,
    checkoutSessionId = null,
    upgradeQuote = false,
    swr: swrOptions,
  } = options;

  const {
    data: priceSettingsResult,
    isLoading: priceSettingsLoading,
    mutate: mutatePriceSettings,
  } = useSWR<StripePriceSettings>(priceSettings ? 'api:/stripe/prices/settings' : null, swrOptions);

  // 合并了分组批量查询（?key=forge|search）和普通列表查询（?limit=&product=&active=）——
  // 两者本来就是同一个 GET /api/stripe/prices，按参数不同分支，没必要拆成两个 useSWR。
  const pricesOpt = typeof prices === 'object' ? prices : undefined;
  const pricesQuery = new URLSearchParams();
  if (pricesOpt?.key) pricesQuery.set('key', pricesOpt.key);
  if (pricesOpt?.limit != null) pricesQuery.set('limit', String(pricesOpt.limit));
  if (pricesOpt?.product) pricesQuery.set('product', pricesOpt.product);
  if (pricesOpt?.active != null) pricesQuery.set('active', String(pricesOpt.active));
  const pricesQueryString = pricesQuery.toString();

  const {
    data: pricesResult,
    isLoading: pricesLoading,
    mutate: mutatePrices,
  } = useSWR<PriceListResponse>(
    prices ? `api:/stripe/prices${pricesQueryString ? `?${pricesQueryString}` : ''}` : null,
    { revalidateOnFocus: false, revalidateOnReconnect: true, ...swrOptions },
  );

  const {
    data: priceResult,
    isLoading: priceLoading,
    mutate: mutatePrice,
  } = useSWR<{ price: StripePrice }>(priceId ? `api:/stripe/prices/${priceId}` : null, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    ...swrOptions,
  });

  const {
    data: checkoutSessionResult,
    isLoading: checkoutSessionLoading,
    mutate: mutateCheckoutSession,
  } = useSWR<CheckCheckoutResponse>(
    checkoutSessionId ? `api:/stripe/checkout?session_id=${checkoutSessionId}` : null,
    swrOptions,
  );

  const upgradeTargetPriceId = typeof upgradeQuote === 'object' ? upgradeQuote.targetPriceId : undefined;
  const {
    data: upgradeQuoteResult,
    isLoading: upgradeQuoteLoading,
    mutate: mutateUpgradeQuote,
  } = useSWR<UpgradeQuoteResponse>(
    upgradeQuote
      ? `api:/stripe/subscriptions/upgrade-payment${upgradeTargetPriceId ? `?target_price_id=${upgradeTargetPriceId}` : ''}`
      : null,
    { revalidateOnFocus: false, revalidateOnReconnect: true, ...swrOptions },
  );

  // apiMiddleware 会替换本地 fetcher，trigger 的 arg 被 spread 为 fetch config，因此不写本地 fetcher
  const { trigger: triggerCreateCheckout, isMutating: creatingCheckout } =
    useSWRMutation<CreateCheckoutResponse>('api:/stripe/checkout');
  const { trigger: triggerCancelSubscription, isMutating: cancelingSubscription } =
    useSWRMutation<CancelSubscriptionResponse>('api:/stripe/subscriptions/cancel');
  const { trigger: triggerExecuteUpgrade, isMutating: upgrading } =
    useSWRMutation<UpgradeExecuteResponse>('api:/stripe/subscriptions/upgrade-payment');

  const createCheckoutSession = useCallback(
    (payload: CreateCheckoutPayload) =>
      triggerCreateCheckout({ method: 'POST', body: JSON.stringify(payload) }),
    [triggerCreateCheckout],
  );

  const cancelSubscription = useCallback(
    (subscriptionId: string) =>
      triggerCancelSubscription({ method: 'POST', body: JSON.stringify({ subscriptionId }) }),
    [triggerCancelSubscription],
  );

  const executeUpgrade = useCallback(
    (payload: ExecuteUpgradePayload) =>
      triggerExecuteUpgrade({ method: 'POST', body: JSON.stringify(payload) }),
    [triggerExecuteUpgrade],
  );

  return {
    // 1. 商品设置 GET /api/stripe/prices/settings
    priceSettings: priceSettingsResult,
    priceSettingsLoading,
    mutatePriceSettings,

    // 2. 商品价格（列表 / 按 settings 分组批量）GET /api/stripe/prices
    prices: pricesResult?.prices,
    pricesLoading,
    mutatePrices,

    // 2b. 单个商品价格 GET /api/stripe/prices/:id
    price: priceResult?.price,
    priceLoading,
    mutatePrice,

    // 4. 支付检查 GET /api/stripe/checkout?session_id=
    checkoutSession: checkoutSessionResult?.session,
    checkoutSessionLoading,
    mutateCheckoutSession,

    // 6. 升级报价 GET /api/stripe/subscriptions/upgrade-payment
    upgradeQuote: upgradeQuoteResult?.data,
    upgradeQuoteLoading,
    mutateUpgradeQuote,

    // 3. 创建支付 POST /api/stripe/checkout
    createCheckoutSession,
    creatingCheckout,

    // 5. 取消订阅 POST /api/stripe/subscriptions/cancel
    cancelSubscription,
    cancelingSubscription,

    // 7. 执行升级 POST /api/stripe/subscriptions/upgrade-payment
    executeUpgrade,
    upgrading,
  };
}

// 支付记录：GET /api/stripe/orders 合并了 Stripe（payment_records 表）和 Apple IAP
// （payment_apple 表）两个来源，按支付/创建时间倒序分页返回。
export interface StripeOrder {
  id: string;
  planName: string | null;
  planType: string | null;
  status: 'paid' | 'canceled' | 'failed' | 'pending' | 'refunded';
  orderId: string;
  date: string;
  /** 已经从"分"换算成"元" */
  amount: number;
  provider: 'stripe' | 'apple';
  /** 仅 provider==='stripe' 时存在 */
  stripeInvoiceUrl?: string | null;
}

interface StripeOrdersPageResponse {
  page: number;
  pageSize: number;
  hasMore: boolean;
  items: StripeOrder[];
}

async function fetchStripeOrders(params: { page: number; pageSize: number }) {
  const query = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  const res = await customFetch(`/api/stripe/orders?${query}`);
  if (!(res instanceof Response)) throw res instanceof Error ? res : new Error('fetch failed');
  return res.json() as Promise<StripeOrdersPageResponse>;
}

export interface UseStripeOrderOptions {
  /** 每页条数，默认 20，跟服务端默认值保持一致 */
  pageSize?: number;
  swr?: SWRInfiniteConfiguration<StripeOrdersPageResponse>;
}

/**
 * 支付记录无限滚动分页，风格与 use-market-entity.ts / use-credits.ts 一致：
 * 数组 key + 独立 fetcher + IntersectionObserver sentinel，替代手动"加载更多"按钮。
 */
export function useStripeOrder(options: UseStripeOrderOptions = {}) {
  const { pageSize = 20, swr: swrOptions } = options;

  const getKey = useCallback(
    (pageIndex: number, previousPageData: StripeOrdersPageResponse | null) => {
      if (previousPageData && !previousPageData.hasMore) return null;
      return ['api:/stripe/orders', pageSize, pageIndex] as const;
    },
    [pageSize],
  );

  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<
    StripeOrdersPageResponse,
    Error
  >(
    getKey,
    ([, size, pageIndex]: readonly [string, number, number]) =>
      fetchStripeOrders({ page: pageIndex + 1, pageSize: size }),
    { revalidateOnFocus: false, revalidateFirstPage: false, ...swrOptions },
  );

  const orders = data?.flatMap((page) => page?.items ?? []) ?? [];
  const lastPage = data?.[data.length - 1];
  const hasMore = lastPage ? lastPage.hasMore : false;

  const loadMore = useCallback(() => {
    if (!isValidating && hasMore) setSize((s) => s + 1);
  }, [isValidating, hasMore, setSize]);

  // IntersectionObserver sentinel：绑定到列表底部元素，进入视口时自动加载下一页
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setSentinelRef = useCallback(
    (el: Element | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!el) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadMore();
        },
        { threshold: 0.1 },
      );
      observerRef.current.observe(el);
    },
    [loadMore],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return {
    orders,
    hasMore,
    isLoading,
    isValidating,
    error,
    page: size,
    loadMore,
    setSentinelRef,
    mutateOrders: mutate,
  };
}
